import {
  isAsOrAsasOption,
  isNetflixBuyAndSell,
  isOptionActive,
  isOptionAvailable,
  Option,
  OptionStatus,
  Product,
  TvProduct,
} from '@dch/products-business-logic';
import { OrderConfirmationModalComponent } from '../../details/option-details/order-confirmation-modal/order-confirmation-modal.component';
import {
  AnalyticsEventData,
  LanguageService,
  TranslationService,
} from '@proximus/ng-common';
import {
  Alert,
  AlertsService,
  LevelType,
  MessageType,
  ModalService,
  PxModalMessageService,
} from '@proximus/ng-common-components';
import { getOfferListFromStorage } from '../webshop/webshop-utils';
import { DISNEY_PLUS_MPUID_CODES, SPORTS_MPUID_CODES } from 'src/app/details/constants';

const DEFAULT_IFF_OPTION_LOCKTIME = 0;
const DEFAULT_STP_OPTION_LOCKTIME = 900; // 15 minutes

function buildKey(prefix: string, selectedProduct: Product, option: Option) {
  if (selectedProduct.accessNumber && option) {
    return (
      prefix +
      selectedProduct.accessNumber +
      '-' +
      option.optionCodeForDescription
    );
  } else {
    return null;
  }
}

export function buildLockKey(selectedProduct: Product, option: Option) {
  return buildKey('lock-', selectedProduct, option);
}

export function buildConfigurationLockKey(
  selectedProduct: Product,
  option: Option,
) {
  return buildKey('configurationLock-', selectedProduct, option);
}

// lockTime is configure in supportedOptions in Magnolia
// the accepted format are :
//  - xxh for hours
//  - xxm for minutes
//  - xxh for seconds
// if not a correct format:
// - 2grh => convert in 2h => 7200s
// - xx2grh => is not a number => defaultLockTime
// reminder: +value ( + is unary plus operator to convert to a number)
export function convertStringLockTimeToSeconds(
  lockTime: string,
  defaultLockTime: number,
): number {
  if (lockTime.toLowerCase().endsWith('h')) {
    return (
      +lockTime.substring(0, lockTime.length - 1) * 60 * 60 || defaultLockTime
    );
  }
  if (lockTime.toLowerCase().endsWith('m')) {
    return +lockTime.substring(0, lockTime.length - 1) * 60 || defaultLockTime;
  }
  if (lockTime.toLowerCase().endsWith('s')) {
    return +lockTime.substring(0, lockTime.length - 1) || defaultLockTime;
  }
  return defaultLockTime;
}

export function isIffOption(option: Option) {
  return option?.orderType === 'IFF' || option?.orderType === 'IFF-one';
}

export function isStpOption(option: Option, isSwap: boolean) {
  return option.orderType === 'STP' || isSwap;
}

function isLockFromSessionStorage(selectedProduct: Product, option: Option) {
  const value = sessionStorage.getItem(buildLockKey(selectedProduct, option));
  const lockDate = +value;
  let lockTime = isIffOption(option)
    ? DEFAULT_IFF_OPTION_LOCKTIME
    : DEFAULT_STP_OPTION_LOCKTIME;
  if (option?.lockTime) {
    lockTime = convertStringLockTimeToSeconds(option.lockTime, lockTime);
  }

  if (lockDate) {
    const diff = diffSeconds(Date.now(), lockDate);
    return diff <= lockTime;
  } else {
    return false;
  }
}

function isConfigurationLockFromSessionStorage(
  selectedProduct: Product,
  option: Option,
) {
  const value = sessionStorage.getItem(
    buildConfigurationLockKey(selectedProduct, option),
  );
  const lockDate = +value;
  if (lockDate) {
    const diff = diffSeconds(Date.now(), lockDate);
    return diff <= 300; // 5 minutes
  } else {
    return false;
  }
}

function diffSeconds(dt2: number, dt1: number) {
  return (dt2 - dt1) / 1000;
}

export function isPending(
  selectedProduct: Product,
  option: Option,
  isAiaPending: boolean,
) {
  return (
    isLockFromSessionStorage(selectedProduct, option) ||
    isConfigurationLockFromSessionStorage(selectedProduct, option) ||
    !!option.locked ||
    !!option.configurationLocked ||
    isAiaPending
  );
}

export function getAlertKey(deepLinkMpcUuid: string, option: Option): string {
  return (
    (deepLinkMpcUuid ? deepLinkMpcUuid : option.optionCodeForDescription) +
    'alertLevel3'
  );
}

function dispatchAlert(
  type: 'error' | 'info' | 'success' | 'warning',
  title: string,
  description: string,
  deepLinkMpcUuid: string,
  option: Option,
  alertsService: AlertsService,
  useNegativeColors: boolean,
  taggingData?: AnalyticsEventData,
): void {
  const alert: Alert = {
    isClosable: true,
    alertKey: getAlertKey(deepLinkMpcUuid, option),
    title,
    description,
    type,
    useNegativeColors,
    taggingData,
  };
  alertsService.dispatchAlert(alert);
}

export function onResult(
  event,
  deepLinkMpcUuid: string,
  selectedProduct: Product,
  option: Option,
  translationService: TranslationService,
  languageService: LanguageService,
  modalService: ModalService,
  pxModalMessageService: PxModalMessageService,
  alertsService: AlertsService,
  treatmentCode?: string,
  useNegativeColors?: boolean,
  skipPendingAlerts?: boolean,
  taggingData?: AnalyticsEventData,
) {
  if (event && event.type && event.type === 'success') {
    if (event.action && event.action === 'add') {
      let message =
        option.trialOption?.endOfTrialDecision === 'CTP' ||
          option.ibStatus === OptionStatus.ACTIVE
          ? translationService.getTranslatedValueFor(
            'option.details.ceasing.description',
          )
          : translationService.getTranslatedValueFor(
            'message.confirmation.description',
          );
      if (option.showPersonalizedMessage) {
        message = translationService.getTranslatedValueFor(
          'message.confirmation.mpay.description',
        );
      }
      if (option.cappingTempCeaseMessage && option.isReverse) {
        message =
          option.cappingTempCeaseMessage[languageService.getSelectedLanguage()];
      }
      if (option.cappingTempProvideMessage && !option.isReverse) {
        message =
          option.cappingTempProvideMessage[
          languageService.getSelectedLanguage()
          ];
      }
      if (isNetflixBuyAndSell(option)) {
        modalService
          .create<OrderConfirmationModalComponent>(
            OrderConfirmationModalComponent,
          )
          .subscribe((componentRef) => {
            componentRef.instance.packDescription = option.packDescription;
            componentRef.instance.netflixBuyAndSellStatus = (
              selectedProduct as TvProduct
            ).netflixBuyAndSell;
            componentRef.instance.dispatchAlert = () =>
              setTimeout(
                () =>
                  dispatchAlert(
                    event.type,
                    translationService.getTranslatedValueFor(
                      'message.confirmation.title',
                    ),
                    translationService.getTranslatedValueFor(
                      'message.confirmation.description',
                    ),
                    deepLinkMpcUuid,
                    option,
                    alertsService,
                    useNegativeColors,
                    taggingData,
                  ),
                800,
              );
          });
      } else if (option.orderConfirmationMessage && isOptionAvailable(option)) {
        if (isAsOrAsasActiveForNetflix(selectedProduct, option)) {
          pxModalMessageService.dispatchMessage(
            MessageType.Warning,
            LevelType.Root,
            translationService.getTranslatedValueFor(
              'option.details.netflix.already.active.popup.message',
            ),
            translationService.getTranslatedValueFor(
              'option.details.netflix.already.active.popup.title',
            ),
            false,
            true,
            undefined,
            true,
            () =>
              setTimeout(
                () =>
                  dispatchAlert(
                    event.type,
                    translationService.getTranslatedValueFor(
                      'message.confirmation.title',
                    ),
                    translationService.getTranslatedValueFor(
                      'message.confirmation.description',
                    ),
                    deepLinkMpcUuid,
                    option,
                    alertsService,
                    useNegativeColors,
                    taggingData,
                  ),
                800,
              ),
            true,
            translationService.getTranslatedValueFor(
              'option.details.netflix.already.active.popup.button',
            ),
          );
        } else {
          pxModalMessageService.dispatchMessage(
            MessageType.Warning,
            LevelType.Root,
            option.orderConfirmationMessage[
            languageService.getSelectedLanguage()
            ],
            translationService.getTranslatedValueFor('message.warning.title'),
            false,
            true,
            undefined,
            true,
            () =>
              setTimeout(
                () =>
                  dispatchAlert(
                    event.type,
                    translationService.getTranslatedValueFor(
                      'message.confirmation.title',
                    ),
                    option.trialOption?.endOfTrialDecision === 'CTP' ||
                      option.ibStatus === OptionStatus.ACTIVE
                      ? translationService.getTranslatedValueFor(
                        'option.details.ceasing.description',
                      )
                      : translationService.getTranslatedValueFor(
                        'message.confirmation.description',
                      ),
                    deepLinkMpcUuid,
                    option,
                    alertsService,
                    useNegativeColors,
                    taggingData,
                  ),
                800,
              ),
            true,
            translationService.getTranslatedValueFor(
              'option.details.order.confirmation.popup.button',
            ),
          );
        }
      } else {
        dispatchAlert(
          event.type,
          translationService.getTranslatedValueFor(
            'message.confirmation.title',
          ),
          message,
          deepLinkMpcUuid,
          option,
          alertsService,
          useNegativeColors,
          taggingData,
        );
      }
    } else if (event.action && event.action === 'remove') {
      dispatchAlert(
        event.type,
        translationService.getTranslatedValueFor('message.confirmation.title'),
        option.trialOption?.endOfTrialDecision === 'CTP' ||
          option.ibStatus === OptionStatus.ACTIVE
          ? translationService.getTranslatedValueFor(
            'option.details.ceasing.description',
          )
          : translationService.getTranslatedValueFor(
            'message.confirmation.description',
          ),
        deepLinkMpcUuid,
        option,
        alertsService,
        useNegativeColors,
        taggingData,
      );
    } else if (event.action && event.action === 'configure') {
      dispatchAlert(
        event.type,
        translationService.getTranslatedValueFor('message.confirmation.title'),
        option.trialOption?.endOfTrialDecision === 'CTP' ||
          option.ibStatus === OptionStatus.ACTIVE
          ? translationService.getTranslatedValueFor(
            'option.details.ceasing.description',
          )
          : translationService.getTranslatedValueFor(
            'message.confirmation.description',
          ),
        deepLinkMpcUuid,
        option,
        alertsService,
        useNegativeColors,
        taggingData,
      );
    }
    if (treatmentCode !== undefined) {
      const offerListFromStorage = getOfferListFromStorage(treatmentCode);
      setTimeout(
        () => (window.location.href = offerListFromStorage.backUrl.success),
        5000,
      );
    }
  } else if (event && event.type && event.type === 'error') {
    if (event.suspended) {
      dispatchAlert(
        event.type,
        translationService.getTranslatedValueFor('message.error.title'),
        translationService.getTranslatedValueFor(
          'message.error.description.frozenAssets',
        ),
        deepLinkMpcUuid,
        option,
        alertsService,
        useNegativeColors,
        taggingData,
      );
    } else if (event.action && event.action === 'add') {
      if (event.noCreditToOrder) {
        dispatchAlert(
          event.type,
          undefined,
          option.noCreditToOrderMessage &&
            option.noCreditToOrderMessage[languageService.getSelectedLanguage()]
            ? option.noCreditToOrderMessage[
            languageService.getSelectedLanguage()
            ]
            : translationService.getTranslatedValueFor(
              'message.error.description',
            ),
          deepLinkMpcUuid,
          option,
          alertsService,
          useNegativeColors,
          taggingData,
        );
      } else if (event.cannotOrder) {
        if (option.optionCodeForDescription === 'DLBWD') {
          dispatchAlert(
            'warning',
            translationService.getTranslatedValueFor('message.warning.title'),
            option.cannotOrderMessage &&
              option.cannotOrderMessage[languageService.getSelectedLanguage()]
              ? option.cannotOrderMessage[languageService.getSelectedLanguage()]
              : translationService.getTranslatedValueFor(
                'message.error.description',
              ),
            deepLinkMpcUuid,
            option,
            alertsService,
            useNegativeColors,
          );
        } else {
          dispatchAlert(
            event.type,
            translationService.getTranslatedValueFor('message.error.title'),
            option.cannotOrderMessage &&
              option.cannotOrderMessage[languageService.getSelectedLanguage()]
              ? option.cannotOrderMessage[languageService.getSelectedLanguage()]
              : translationService.getTranslatedValueFor(
                'message.error.description',
              ),
            deepLinkMpcUuid,
            option,
            alertsService,
            useNegativeColors,
            taggingData,
          );
        }
      } else {
        dispatchAlert(
          event.type,
          translationService.getTranslatedValueFor('message.error.title'),
          getDescriptionMessage(event, translationService),
          deepLinkMpcUuid,
          option,
          alertsService,
          useNegativeColors,
          taggingData,
        );
      }
    } else if (event.action && event.action === 'remove') {
      if (event.lastOptionNotCeasable) {
        dispatchAlert(
          'warning',
          translationService.getTranslatedValueFor('message.warning.title'),
          translationService.getTranslatedValueFor(
            'message.error.description.lastOptionNotCeasable',
          ),
          deepLinkMpcUuid,
          option,
          alertsService,
          useNegativeColors,
          taggingData,
        );
      } else if (event.ceaseNotAllowed) {
        dispatchAlert(
          'warning',
          translationService.getTranslatedValueFor('message.warning.title'),
          option.ceaseNotAllowedMessage &&
            option.ceaseNotAllowedMessage[languageService.getSelectedLanguage()]
            ? option.ceaseNotAllowedMessage[
            languageService.getSelectedLanguage()
            ]
            : translationService.getTranslatedValueFor(
              'message.error.description.ceaseNotAllowed',
            ),
          deepLinkMpcUuid,
          option,
          alertsService,
          useNegativeColors,
          taggingData,
        );
      } else {
        dispatchAlert(
          event.type,
          translationService.getTranslatedValueFor('message.error.title'),
          translationService.getTranslatedValueFor('message.error.description'),
          deepLinkMpcUuid,
          option,
          alertsService,
          useNegativeColors,
          taggingData,
        );
      }
    } else if (event.action && event.action === 'configure') {
      dispatchAlert(
        event.type,
        translationService.getTranslatedValueFor('message.error.title'),
        event.cwc
          ? translationService.getTranslatedValueFor(
            'message.error.description.cwc',
          )
          : translationService.getTranslatedValueFor(
            'message.error.description',
          ),
        deepLinkMpcUuid,
        option,
        alertsService,
        useNegativeColors,
        taggingData,
      );
    } else {
      // generic error fallback
      dispatchAlert(
        event.type,
        translationService.getTranslatedValueFor('message.error.title'),
        translationService.getTranslatedValueFor('message.error.description'),
        deepLinkMpcUuid,
        option,
        alertsService,
        useNegativeColors,
        taggingData,
      );
    }
    if (treatmentCode !== undefined) {
      const offerListFromStorage = getOfferListFromStorage(treatmentCode);
      setTimeout(
        () => (window.location.href = offerListFromStorage.backUrl.failure),
        5000,
      );
    }
  } else if (
    event &&
    event.type &&
    event.type === 'warning' &&
    !skipPendingAlerts
  ) {
    if (event.action && event.action === 'pending') {
      dispatchAlert(
        event.type,
        translationService.getTranslatedValueFor(
          'message.warning.pending.title',
        ),
        translationService.getTranslatedValueFor(
          'message.warning.pending.description',
        ),
        deepLinkMpcUuid,
        option,
        alertsService,
        useNegativeColors,
      );
    } else if (event.action && event.action === 'aiaPending') {
      dispatchAlert(
        event.type,
        translationService.getTranslatedValueFor(
          'message.warning.aiaPending.title',
        ),
        translationService.getTranslatedValueFor(
          'message.warning.aiaPending.description',
        ),
        deepLinkMpcUuid,
        option,
        alertsService,
        useNegativeColors,
      );
    }
  }
}

export function getDescriptionMessage(event, translationService) {
  if (event.cwc) {
    return translationService.getTranslatedValueFor(
      'message.error.description.cwc',
    );
  } else if (event.underage) {
    return translationService.getTranslatedValueFor(
      'message.error.description.underage',
    );
  } else {
    return translationService.getTranslatedValueFor(
      'message.error.description',
    );
  }
}

function isAsOrAsasActiveForNetflix(product: Product, option: Option) {
  if (product && option && isAsOrAsasOption(option)) {
    return product.options.find(
      (o: Option) =>
        (isAsOrAsasOption(o) || isNetflixBuyAndSell(o)) && isOptionActive(o),
    );
  }
  return false;
}

export function hasAsOrAsasActive(product: Product) {
  if (product) {
    return product.options.some(
      (o: Option) => isAsOrAsasOption(o) && isOptionActive(o),
    );
  }
  return false;
}

interface RetrieveProductAndOptionFromConfirmationEventResult {
  isSportsProduct?: boolean ,isDisneyPlusProduct?: boolean, currentProduct?: any[], currentOption?: any[];
}

function checkSpecialProducts(productDetails: any[]): { isDisneyPlusProduct?: boolean, isSportsProduct?: boolean } {
  for (const productDetail of productDetails) {
    if (productDetail.products.some((product: any) => DISNEY_PLUS_MPUID_CODES.includes(product.id))) {
      return { isDisneyPlusProduct: true };
    }
    if (productDetail.products.some((product: any) => SPORTS_MPUID_CODES.includes(product.id))) {
      return { isSportsProduct: true };
    }
  }
  return {};
}

function getMatchingProducts(productDetails: any[], selectedProduct: Product, mpcUuid: string) {
  const currentProduct: any[] = [];
  const currentOption: any[] = [];

  for (const productDetail of productDetails) {
    const matchingProduct = productDetail.products.find(
      (product: any) =>
        product.id === selectedProduct.groupMpcUuid ||
        product.id === selectedProduct.parentProductMpcUuid
    );
    if (matchingProduct) {
      currentProduct.push(matchingProduct);
    } else {
      const matchingOption = productDetail.products.find(
        (product: any) => product.id === mpcUuid
      );
      if (matchingOption) {
        currentOption.push(matchingOption);
      }
    }
  }

  // If we have main products, find the option within them
  if (currentProduct.length > 0) {
    for (const item of currentProduct) {
      const foundOption = item.products?.find(
        (product: any) => product.id === mpcUuid
      );
      if (foundOption) {
        currentOption.push(foundOption);
        break;
      }
    }
  }

  return { currentProduct, currentOption };
}

export function retrieveProductAndOptionFromConfirmationEvent(
  selectedProduct: Product,
  mpcUuid: string,
  productDetails: any,
): RetrieveProductAndOptionFromConfirmationEventResult {
  if (!productDetails) {
    return {};
  }

  const specialProducts = checkSpecialProducts(productDetails);
  if (specialProducts.isDisneyPlusProduct) return { isDisneyPlusProduct: true };
  if (specialProducts.isSportsProduct) return { isSportsProduct: true };

  return getMatchingProducts(productDetails, selectedProduct, mpcUuid);
}
